import React, {
  useMemo, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { DocumentType } from '@marageti/z4-sdk/lib/documents/Document';
import { useSnackbar } from 'notistack';
import { Partner, TripMatch } from '@marageti/z4-sdk/lib/travel';
import DocsView from './docs-view';
import useDocumentDownload from '../../../hooks/use-document-download';
import { useAuthStore } from '../../../store';
import debugPromise from '../../../utils/debug-promise';

const DocsContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { tripMatchId } = useParams();
  const apiClient = useApiClient();
  const [onDownloadDocument, docError] = useDocumentDownload();
  const { loggedInTraveler } = useAuthStore();

  const {
    data: docs,
    isLoading,
    mutate,
  } = useFetch<Doc[]>(apiClient.documentsClient.getDocuments, [{ params: { trip_match: tripMatchId, all: true } }], !!tripMatchId);

  const { data: tripMatch } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const { data: partner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [tripMatch?.partnerId], !!tripMatch?.partnerId);

  const [submissionError, setSubmissionError] = useState<ApiError>();
  const [docToDelete, setDocToDelete] = useState<Doc | null>(null);
  const [showModal, setShowModal] = useState(false);
  const toggleModal = () => setShowModal((prev) => !prev);

  const onCancelDelete = () => {
    setDocToDelete(null);
    setSubmissionError(undefined);
  };

  const onClickDocument = (doc: Doc) => {
    if (doc.sourceURI) {
      window.open(doc.sourceURI, '_blank');
    } else {
      debugPromise('docsContainer:onClickDocument.download', onDownloadDocument(doc));
    }
  };

  const {
    generalDocuments,
    sampleItineraryDocuments,
  } = useMemo(
    () => {
      const sortedDocuments = docs?.sort((a, b) => {
        // sort by date created
        if (a.createdAt > b.createdAt) return -1;
        if (a.createdAt < b.createdAt) return 1;

        // if dates are equal, sort by title
        if (a.title > b.title) return 1;
        if (a.title < b.title) return -1;

        return 0;
      });

      return {
        generalDocuments: sortedDocuments?.filter((doc: Doc) => doc.documentType === DocumentType.DocumentGeneral),
        sampleItineraryDocuments: sortedDocuments?.filter((doc: Doc) => doc.documentType === DocumentType.DocumentSampleItinerary),
      };
    },
    [docs],
  );

  const onConfirmDelete = async () => {
    try {
      if (docToDelete?.id) await apiClient.documentsClient.deleteDocument(docToDelete.id);
      enqueueSnackbar('Document removed.', { variant: 'success' });
      debugPromise('docsContainer:onConfirmDelete.mutate', mutate());
    } catch (err) {
      setSubmissionError(err as ApiError);
    } finally {
      setDocToDelete(null);
    }
  };

  const handleDelete = (doc: Doc) => {
    setDocToDelete(doc);
  };

  return (
    <DocsView
      docs={generalDocuments as Doc[]}
      docError={docError}
      isLoading={isLoading}
      itineraries={sampleItineraryDocuments as Doc[]}
      docToDelete={docToDelete}
      handleDelete={handleDelete}
      onCancelDelete={onCancelDelete}
      onClickDocument={onClickDocument}
      onConfirmDelete={onConfirmDelete}
      submissionError={submissionError}
      travelerId={loggedInTraveler?.id}
      toggleModal={toggleModal}
      showModal={showModal}
      updateDocs={mutate}
      partner={partner}
    />
  );
};

export default DocsContainer;
